'use client';

import Link from 'next/link';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiPost } from '@/lib/api/client';
import { usePermission } from '@/lib/contexts/PermissionContext';

/**
 * 規約改定時の再同意モーダル(LEGAL)。
 * /auth/me の NeedsTermsReacceptance=true のとき表示し、
 * POST /terms/accept で同意するまで主要操作をブロックする。
 */
export function TermsReacceptanceModal() {
  const { needsTermsReacceptance, refreshRole } = usePermission();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async () => {
    setIsSubmitting(true);

    const result = await apiPost('/terms/accept');

    if (result.error) {
      toast({ description: result.error, variant: 'destructive' });
      setIsSubmitting(false);
      return;
    }

    // 同意を反映するためにサーバから再取得し、フラグを解除する
    await refreshRole();
    setIsSubmitting(false);
  };

  // 閉じる手段を与えない(同意するまでブロック)。
  // open を制御し onOpenChange を渡さないため、外側クリック/ESC/×では閉じない。
  // ×ボタン(DialogContent 直下)は [&>button]:hidden で非表示にする。
  return (
    <Dialog open={needsTermsReacceptance}>
      <DialogContent
        className="sm:max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>利用規約が更新されました</DialogTitle>
          <DialogDescription>
            サービスを継続してご利用いただくには、最新の
            <Link href="/terms" target="_blank" className="mx-1 text-blue-700 hover:underline">
              利用規約
            </Link>
            および
            <Link href="/privacy" target="_blank" className="mx-1 text-blue-700 hover:underline">
              プライバシーポリシー
            </Link>
            への同意が必要です。
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            onClick={handleAccept}
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white hover:bg-blue-700"
          >
            {isSubmitting ? '処理中...' : '同意して続ける'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
